import React, { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import { SYM } from "../utils/textSymbols";
import "./EventModal.css";

function RepeatsModal({
  recurrence,
  startDate,
  onDone,
  onCancel,
  onOpenCustom,
  weekdays,
  weekdayLabels,
  months,
  ordinalSuffix,
}) {
  const [option, setOption] = useState(() => {
    if (!recurrence || recurrence.frequency === "none") return "none";
    if (recurrence.frequency === "daily") return "daily";
    if (recurrence.frequency === "weekly" && recurrence.interval === 2 && recurrence.byDay?.length === 1) return "biweekly";
    if (recurrence.frequency === "weekly") return "weekly";
    if (recurrence.frequency === "monthly") return "monthly";
    if (recurrence.frequency === "yearly") return "yearly";
    if (recurrence.frequency === "custom") return "custom";
    return "none";
  });
  const [weeklyDays, setWeeklyDays] = useState(() => {
    if (recurrence?.frequency === "weekly" && recurrence.byDay?.length) return [...recurrence.byDay];
    if (startDate) {
      const d = new Date(startDate + "T12:00:00");
      return [weekdays[d.getDay()]];
    }
    return ["MO"];
  });
  const [biweeklyDay, setBiweeklyDay] = useState(() => {
    if (recurrence?.frequency === "weekly" && recurrence.interval === 2 && recurrence.byDay?.[0]) return recurrence.byDay[0];
    if (startDate) {
      const d = new Date(startDate + "T12:00:00");
      return weekdays[d.getDay()];
    }
    return "MO";
  });

  const toggleWeekDay = (w) => {
    setWeeklyDays(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b)));
  };

  const buildRecurrence = () => {
    if (option === "none") return null;
    if (option === "daily") return { frequency: "daily" };
    if (option === "weekly") return { frequency: "weekly", byDay: weeklyDays.length ? weeklyDays : [biweeklyDay], interval: 1 };
    if (option === "biweekly") return { frequency: "weekly", interval: 2, byDay: [biweeklyDay] };
    if (option === "monthly" && startDate) {
      const day = parseInt(startDate.split("-")[2], 10);
      return { frequency: "monthly", byMonthDay: day };
    }
    if (option === "yearly" && startDate) {
      const [, monthStr, dayStr] = startDate.split("-");
      return { frequency: "yearly", byMonth: parseInt(monthStr, 10), byMonthDay: parseInt(dayStr, 10) };
    }
    if (option === "custom") {
      onOpenCustom();
      return undefined;
    }
    return null;
  };

  const handleDone = () => {
    if (option === "custom") {
      onOpenCustom();
      return;
    }
    const r = buildRecurrence();
    if (r !== undefined) onDone(r);
  };

  return (
    <div className="event-modal-overlay recurrence-overlay" onClick={onCancel}>
      <div className="recurrence-modal" onClick={e => e.stopPropagation()}>
        <div className="recurrence-modal-header">
          <span>Repeats</span>
          <button type="button" className="recurrence-modal-close" onClick={onCancel} aria-label="Close">{SYM.close}</button>
        </div>
        <div className="recurrence-modal-body">
          {["none", "daily", "weekly", "biweekly", "monthly", "yearly", "custom"].map((opt) => (
            <div key={opt} className="recurrence-option-row">
              <label className="recurrence-radio-label">
                <input
                  type="radio"
                  name="repeats"
                  checked={option === opt}
                  onChange={() => setOption(opt)}
                />
                <span>
                  {opt === "none" && "Does not repeat"}
                  {opt === "daily" && "Every day"}
                  {opt === "weekly" && "Every week on:"}
                  {opt === "biweekly" && "Every other week on"}
                  {opt === "monthly" && startDate && `Every month on the ${ordinalSuffix(parseInt(startDate.split("-")[2], 10))}`}
                  {opt === "yearly" && startDate && (() => {
                    const [, m, d] = startDate.split("-");
                    return `Every year on ${months[parseInt(m, 10) - 1]} ${ordinalSuffix(parseInt(d, 10))}`;
                  })()}
                  {opt === "custom" && `Custom ${SYM.arrowRight}`}
                </span>
              </label>
              {opt === "weekly" && option === "weekly" && (
                <div className="recurrence-day-pills">
                  {weekdays.map((w, i) => (
                    <button
                      key={w}
                      type="button"
                      className={`recurrence-day-pill ${weeklyDays.includes(w) ? "active" : ""}`}
                      onClick={() => toggleWeekDay(w)}
                    >
                      {weekdayLabels[i]}
                    </button>
                  ))}
                </div>
              )}
              {opt === "biweekly" && option === "biweekly" && (
                <div className="recurrence-day-pills">
                  {weekdays.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`recurrence-day-pill ${biweeklyDay === w ? "active" : ""}`}
                      onClick={() => setBiweeklyDay(w)}
                    >
                      {weekdayLabels[weekdays.indexOf(w)]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="recurrence-modal-footer">
          <button type="button" className="recurrence-btn secondary" onClick={onCancel}>CANCEL</button>
          <button type="button" className="recurrence-btn primary" onClick={handleDone}>DONE</button>
        </div>
      </div>
    </div>
  );
}

function CustomRecurrenceModal({
  recurrence,
  startDate,
  onSave,
  onCancel,
  weekdays,
  weekdayLabels,
  ordinals,
  months,
  ordinalSuffix,
}) {
  const intervalUnitOptions = [
    { value: "days", label: "days" },
    { value: "weeks", label: "weeks" },
    { value: "months", label: "months" },
    { value: "years", label: "years" },
  ];
  const [interval, setInterval] = useState(recurrence?.interval ?? 1);
  const [intervalUnit, setIntervalUnit] = useState(recurrence?.intervalUnit ?? "weeks");
  const startDayOfWeek = startDate ? weekdays[new Date(startDate + "T12:00:00").getDay()] : "MO";
  const customByDayFallback = Array.isArray(recurrence?.byDay) && recurrence.byDay.length ? recurrence.byDay[0] : startDayOfWeek;
  const [customByDayList, setCustomByDayList] = useState(recurrence?.byDay ?? [startDayOfWeek]);
  const [monthOnKind, setMonthOnKind] = useState(
    recurrence?.byMonthDay != null ? "day" : "ordinal"
  );
  const [monthDay, setMonthDay] = useState(recurrence?.byMonthDay ?? (startDate ? parseInt(startDate.split("-")[2], 10) : 1));
  const [monthOrdinal, setMonthOrdinal] = useState(recurrence?.bySetPos ?? 1);
  const [monthWeekday, setMonthWeekday] = useState(Array.isArray(recurrence?.byDay) ? recurrence.byDay[0] : recurrence?.byDay ?? startDayOfWeek);
  const [yearOnKind, setYearOnKind] = useState(
    recurrence?.bySetPos != null && recurrence?.byDay ? "ordinal" : "date"
  );
  const [yearMonth, setYearMonth] = useState(recurrence?.byMonth ?? (startDate ? parseInt(startDate.split("-")[1], 10) : 1));
  const [yearDay, setYearDay] = useState(recurrence?.byMonthDay ?? (startDate ? parseInt(startDate.split("-")[2], 10) : 1));
  const [yearOrdinal, setYearOrdinal] = useState(recurrence?.bySetPos ?? 1);
  const [yearWeekday, setYearWeekday] = useState(Array.isArray(recurrence?.byDay) ? recurrence.byDay[0] : recurrence?.byDay ?? startDayOfWeek);
  const [yearMonthOrdinal, setYearMonthOrdinal] = useState(recurrence?.byMonth ?? (startDate ? parseInt(startDate.split("-")[1], 10) : 1));

  const toggleCustomDay = (w) => {
    setCustomByDayList(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b)));
  };

  const buildCustomRecurrence = () => {
    const base = { frequency: "custom", interval, intervalUnit };
    if (intervalUnit === "weeks") {
      return { ...base, frequency: "weekly", interval, byDay: customByDayList.length ? customByDayList : [customByDayFallback] };
    }
    if (intervalUnit === "months") {
      if (monthOnKind === "day") {
        return { ...base, frequency: "monthly", interval, byMonthDay: monthDay };
      }
      return { ...base, frequency: "monthly", interval, bySetPos: monthOrdinal, byDay: monthWeekday };
    }
    if (intervalUnit === "years") {
      if (yearOnKind === "date") {
        return { ...base, frequency: "yearly", interval, byMonth: yearMonth, byMonthDay: yearDay };
      }
      return { ...base, frequency: "yearly", interval, bySetPos: yearOrdinal, byDay: yearWeekday, byMonth: yearMonthOrdinal };
    }
    return { ...base, frequency: "daily", interval };
  };

  return (
    <div className="event-modal-overlay recurrence-overlay" onClick={onCancel}>
      <div className="recurrence-modal recurrence-modal-custom" onClick={e => e.stopPropagation()}>
        <div className="recurrence-modal-header">
          <span>Custom recurrence</span>
          <button type="button" className="recurrence-modal-close" onClick={onCancel} aria-label="Close">{SYM.close}</button>
        </div>
        <div className="recurrence-modal-body">
          <div className="recurrence-custom-row">
            <span>Repeats every</span>
            <input
              type="number"
              min={1}
              value={interval}
              onChange={e => setInterval(parseInt(e.target.value, 10) || 1)}
              className="recurrence-number-input"
            />
            {intervalUnitOptions.map(({ value, label }) => (
              <label key={value} className="recurrence-radio-inline">
                <input
                  type="radio"
                  name="intervalUnit"
                  checked={intervalUnit === value}
                  onChange={() => setIntervalUnit(value)}
                />
                {label}
              </label>
            ))}
          </div>
          {intervalUnit === "weeks" && (
            <div className="recurrence-custom-row">
              <span>On:</span>
              <div className="recurrence-day-pills">
                {weekdays.map((w, i) => (
                  <button
                    key={w}
                    type="button"
                    className={`recurrence-day-pill ${customByDayList.includes(w) ? "active" : ""}`}
                    onClick={() => toggleCustomDay(w)}
                  >
                    {weekdayLabels[i]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {intervalUnit === "months" && (
            <div className="recurrence-custom-row">
              <span>On:</span>
              <label className="recurrence-radio-inline">
                <input type="radio" name="monthOn" checked={monthOnKind === "day"} onChange={() => setMonthOnKind("day")} />
                the <input type="number" min={1} max={31} value={monthDay} onChange={e => setMonthDay(parseInt(e.target.value, 10) || 1)} className="recurrence-number-inline" />
              </label>
              <label className="recurrence-radio-inline">
                <input type="radio" name="monthOn" checked={monthOnKind === "ordinal"} onChange={() => setMonthOnKind("ordinal")} />
                <select value={monthOrdinal} onChange={e => setMonthOrdinal(parseInt(e.target.value, 10))}>
                  {ordinals.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={monthWeekday} onChange={e => setMonthWeekday(e.target.value)}>
                  {weekdays.map((w, i) => <option key={w} value={w}>{weekdayLabels[i]}</option>)}
                </select>
              </label>
            </div>
          )}
          {intervalUnit === "years" && (
            <div className="recurrence-custom-row">
              <span>On:</span>
              <label className="recurrence-radio-inline">
                <input type="radio" name="yearOn" checked={yearOnKind === "date"} onChange={() => setYearOnKind("date")} />
                <select value={yearMonth} onChange={e => setYearMonth(parseInt(e.target.value, 10))}>
                  {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" min={1} max={31} value={yearDay} onChange={e => setYearDay(parseInt(e.target.value, 10) || 1)} className="recurrence-number-inline" />
                <span> ({ordinalSuffix(yearDay)})</span>
              </label>
              <label className="recurrence-radio-inline">
                <input type="radio" name="yearOn" checked={yearOnKind === "ordinal"} onChange={() => setYearOnKind("ordinal")} />
                the <select value={yearOrdinal} onChange={e => setYearOrdinal(parseInt(e.target.value, 10))}>
                  {ordinals.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={yearWeekday} onChange={e => setYearWeekday(e.target.value)}>
                  {weekdays.map((w, i) => <option key={w} value={w}>{weekdayLabels[i]}</option>)}
                </select>
                in <select value={yearMonthOrdinal} onChange={e => setYearMonthOrdinal(parseInt(e.target.value, 10))}>
                  {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>
        <div className="recurrence-modal-footer">
          <button type="button" className="recurrence-btn secondary" onClick={onCancel}>CANCEL</button>
          <button type="button" className="recurrence-btn primary" onClick={() => onSave(buildCustomRecurrence())}>SAVE</button>
        </div>
      </div>
    </div>
  );
}

export default function EventModal({
  event,
  selectedDate,
  onClose,
  onSave,
  onDelete,
  onCopy,
  participants: allParticipants,
  initialFormData = null,
  isSaving = false,
  isDeleting = false
}) {
  const isNewEvent = !event;
  const REMINDER_PRESETS = [
    { value: 0, label: "None" },
    { value: 15, label: "15 minutes before" },
    { value: 60, label: "1 hour before" },
    { value: 1440, label: "1 day before" },
    { value: 4320, label: "3 days before" },
  ];

  const [formData, setFormData] = useState({
    title: "",
    location: "",
    notes: "",
    selectedParticipants: [],
    allDay: false,
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    recurrence: null,
    seriesEndDate: "",
    reminders: [0, 0, 0],
    notifyPersonNames: [],
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [repeatsModalOpen, setRepeatsModalOpen] = useState(false);
  const [customRecurrenceOpen, setCustomRecurrenceOpen] = useState(false);
  const overlayRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const [editScope, setEditScope] = useState("day");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteExtent, setDeleteExtent] = useState("day");
  const [deleteNotifyAttendees, setDeleteNotifyAttendees] = useState(false);
  const startTimeOnFocusRef = useRef(null);

  const isRecurringInstance = event?.id && String(event.id).includes("_");

  const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const ORDINALS = [
    { value: 1, label: "first" },
    { value: 2, label: "second" },
    { value: 3, label: "third" },
    { value: 4, label: "fourth" },
    { value: -1, label: "last" },
  ];
  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  function getRecurrenceSummary(recurrence) {
    if (!recurrence || recurrence.frequency === "none") return "Never";
    if (recurrence.frequency === "daily") return "Every day";
    if (recurrence.frequency === "weekly") {
      if (recurrence.interval === 2 && recurrence.byDay?.length === 1) {
        const d = WEEKDAY_LABELS[WEEKDAYS.indexOf(recurrence.byDay[0])];
        return `Every other week on ${d}`;
      }
      if (recurrence.byDay?.length) {
        const days = recurrence.byDay.map(d => WEEKDAY_LABELS[WEEKDAYS.indexOf(d)]).join(", ");
        return `Every week on ${days}`;
      }
      return "Every week";
    }
    if (recurrence.frequency === "monthly") {
      if (recurrence.byMonthDay != null) return `Every month on the ${ordinalSuffix(recurrence.byMonthDay)}`;
      if (recurrence.bySetPos != null && recurrence.byDay) {
        const ord = ORDINALS.find(o => o.value === recurrence.bySetPos)?.label || recurrence.bySetPos;
        const day = WEEKDAY_LABELS[WEEKDAYS.indexOf(recurrence.byDay)];
        return `Every month on the ${ord} ${day}`;
      }
      return "Every month";
    }
    if (recurrence.frequency === "yearly") {
      if (recurrence.byMonth != null && recurrence.byMonthDay != null && !recurrence.byDay)
        return `Every year on ${MONTHS[recurrence.byMonth - 1]} ${ordinalSuffix(recurrence.byMonthDay)}`;
      if (recurrence.bySetPos != null && recurrence.byDay && recurrence.byMonth != null) {
        const ord = ORDINALS.find(o => o.value === recurrence.bySetPos)?.label || recurrence.bySetPos;
        const day = WEEKDAY_LABELS[WEEKDAYS.indexOf(recurrence.byDay)];
        return `Every year on the ${ord} ${day} in ${MONTHS[recurrence.byMonth - 1]}`;
      }
      return "Every year";
    }
    if (recurrence.frequency === "custom") {
      const n = recurrence.interval ?? 1;
      const unit = recurrence.intervalUnit || "weeks";
      const unitLabel = n === 1 ? unit.slice(0, -1) : unit;
      return `Every ${n} ${unitLabel}`;
    }
    return "Never";
  }

  function ordinalSuffix(n) {
    if (n >= 11 && n <= 13) return n + "th";
    const d = n % 10;
    if (d === 1) return n + "st";
    if (d === 2) return n + "nd";
    if (d === 3) return n + "rd";
    return n + "th";
  }

  // Initialize form data from event, initialFormData (copy), or selectedDate
  useEffect(() => {
    if (event) {
      // Editing existing event
      const startDate = parseISO(event.start);
      const endDate = parseISO(event.end);
      // "All" = no specific members: API returns participants ["All"] or empty householdMemberIds
      let selectedParticipants = event.participants || [];
      if (
        (Array.isArray(event.householdMemberIds) && event.householdMemberIds.length === 0) ||
        (selectedParticipants.length === 1 && selectedParticipants[0] === "All")
      ) {
        selectedParticipants = [];
      } else {
        selectedParticipants = selectedParticipants.filter((p) => p !== "All");
      }

      setFormData({
        title: event.title || "",
        location: event.location || "",
        notes: event.notes || "",
        selectedParticipants,
        allDay: event.allDay || false,
        startDate: format(startDate, "yyyy-MM-dd"),
        startTime: format(startDate, "HH:mm"),
        endDate: format(endDate, "yyyy-MM-dd"),
        endTime: format(endDate, "HH:mm"),
        recurrence: event.recurrence ?? null,
        seriesEndDate: event.recurrence?.untilDay ? event.recurrence.untilDay.split("T")[0] : "",
        reminders: (Array.isArray(event.reminders) ? event.reminders : []).slice(0, 3).map(r => r?.minutesBefore ?? 0).concat([0, 0, 0]).slice(0, 3),
        notifyPersonNames: Array.isArray(event.notifyPersonNames) ? event.notifyPersonNames : [],
      });
      setNotesExpanded(!!(event.notes && event.notes.trim()));
    } else if (selectedDate && initialFormData) {
      // Create event with prefilled data (from Copy)
      setFormData({
        title: initialFormData.title || "",
        location: initialFormData.location || "",
        notes: initialFormData.notes || "",
        selectedParticipants: initialFormData.selectedParticipants || [],
        allDay: !!initialFormData.allDay,
        startDate: initialFormData.startDate || "",
        startTime: initialFormData.startTime || "09:00",
        endDate: initialFormData.endDate || "",
        endTime: initialFormData.endTime || "10:00",
        recurrence: initialFormData.recurrence ?? null,
        seriesEndDate: initialFormData.seriesEndDate || "",
        reminders: (Array.isArray(initialFormData.reminders) ? initialFormData.reminders : []).slice(0, 3).map(r => (typeof r === "object" && r?.minutesBefore) ? r.minutesBefore : 0).concat([0, 0, 0]).slice(0, 3),
        notifyPersonNames: [],
      });
      setNotesExpanded(!!(initialFormData.notes && initialFormData.notes.trim()));
      setHasChanges(true);
      return;
    } else if (selectedDate) {
      // Creating new event with pre-filled date
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      setFormData({
        title: "",
        location: "",
        notes: "",
        selectedParticipants: [],
        allDay: false,
        startDate: dateStr,
        startTime: "09:00",
        endDate: dateStr,
        endTime: "10:00",
        recurrence: null,
        seriesEndDate: "",
        reminders: [0, 0, 0],
        notifyPersonNames: [],
      });
      setHasChanges(true); // Allow save immediately for new events
      return;
    }
    setHasChanges(false);
    startTimeOnFocusRef.current = null;
  }, [event, selectedDate, initialFormData]);

  // Lock background scroll while modal is open (prevents iOS scroll bleed-through)
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyOverflow: body.style.overflow,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    return () => {
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.overflow = prev.bodyOverflow;
      body.style.width = prev.bodyWidth;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Keep touch scrolling inside the modal body on mobile
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const block = (e) => {
      if (e.target.closest?.(".event-modal-body, .recurrence-modal-body")) return;
      e.preventDefault();
    };
    el.addEventListener("touchmove", block, { passive: false });
    return () => el.removeEventListener("touchmove", block);
  }, []);

  // When notes expand, scroll them into view above the keyboard
  useEffect(() => {
    if (!notesExpanded || !bodyScrollRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      bodyScrollRef.current?.querySelector("#notes")?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [notesExpanded]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };

      // Only when start DATE changes: shift end date and time by the same delta
      if (field === "startDate") {
        const oldStart = new Date(prev.startDate + "T" + (prev.startTime || "00:00") + ":00");
        const newStart = new Date(value + "T" + (prev.startTime || "00:00") + ":00");
        const deltaMs = newStart.getTime() - oldStart.getTime();

        const oldEnd = new Date(prev.endDate + "T" + (prev.endTime || "00:00") + ":00");
        const newEnd = new Date(oldEnd.getTime() + deltaMs);

        next.endDate = format(newEnd, "yyyy-MM-dd");
        next.endTime = format(newEnd, "HH:mm");
      }

      return next;
    });
    setHasChanges(true);
  };

  const handleStartTimeFocus = () => {
    startTimeOnFocusRef.current = formData.startTime;
  };

  const handleStartTimeBlur = () => {
    const prevStartTime = startTimeOnFocusRef.current;
    if (prevStartTime == null || prevStartTime === formData.startTime) return;

    setFormData(prev => {
      const oldStart = new Date(prev.startDate + "T" + (prevStartTime || "00:00") + ":00");
      const newStart = new Date(prev.startDate + "T" + (prev.startTime || "00:00") + ":00");
      const newEnd = new Date(prev.endDate + "T" + (prev.endTime || "00:00") + ":00");
      const durationMs = newEnd.getTime() - oldStart.getTime();
      const newEndDate = new Date(newStart.getTime() + durationMs);

      return {
        ...prev,
        endDate: format(newEndDate, "yyyy-MM-dd"),
        endTime: format(newEndDate, "HH:mm")
      };
    });
    setHasChanges(true);
  };

  const handleParticipantToggle = (participant) => {
    setFormData(prev => {
      const current = prev.selectedParticipants;
      // When "All" is selected (empty list), picking one person deselects All and selects only that person
      if (current.length === 0) {
        return { ...prev, selectedParticipants: [participant] };
      }
      return {
        ...prev,
        selectedParticipants: current.includes(participant)
          ? current.filter(p => p !== participant)
          : [...current, participant]
      };
    });
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      selectedParticipants: []  // "All" = no members in API
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!hasChanges || isSaving || isDeleting) return; // Do nothing if no changes
    
    if (onSave) {
      const remindersArray = (formData.reminders || []).filter(m => m && m > 0).map(m => ({ minutesBefore: m }));
      let outRecurrence = formData.recurrence && formData.recurrence.frequency !== "none" ? { ...formData.recurrence } : null;
      if (outRecurrence && formData.seriesEndDate) {
        outRecurrence.untilDay = formData.seriesEndDate;
      } else if (outRecurrence) {
        delete outRecurrence.untilDay;
      }
      const newEventData = {
        ...(event || { id: `new-${Date.now()}` }),
        ...formData,
        start: `${formData.startDate}T${formData.startTime}:00`,
        end: `${formData.endDate}T${formData.endTime}:00`,
        participants: formData.selectedParticipants,
        recurrence: outRecurrence,
        reminders: remindersArray.length > 0 ? remindersArray : undefined,
        notifyPersonNames: !isNewEvent && Array.isArray(formData.notifyPersonNames) && formData.notifyPersonNames.length > 0 ? formData.notifyPersonNames : undefined,
      };
      if (isRecurringInstance) newEventData.editScope = editScope;
      onSave(newEventData);
    }
    setHasChanges(false);
  };

  const handleDelete = () => {
    if (isSaving || isDeleting) return;
    if (isRecurringInstance) {
      setDeleteExtent("day");
      setDeleteNotifyAttendees(false);
      setDeleteConfirmOpen(true);
    } else if (onDelete) {
      onDelete(event);
    }
  };

  const handleDeleteConfirm = () => {
    if (!onDelete) return;
    onDelete({
      ...event,
      extent: deleteExtent,
      notifyPersonNames: deleteNotifyAttendees ? (event?.participants || formData.selectedParticipants || []) : null,
    });
    setDeleteConfirmOpen(false);
  };

  const handleCopy = () => {
    if (isSaving || isDeleting) return;
    if (onCopy) {
      onCopy({
        ...formData,
        start: `${formData.startDate}T${formData.startTime}:00`,
        end: `${formData.endDate}T${formData.endTime}:00`,
        participants: formData.selectedParticipants
      });
    }
  };

  const hasActiveRecurrence = formData.recurrence && formData.recurrence.frequency && formData.recurrence.frequency !== "none";
  const showSeriesEndDate = isNewEvent || !isRecurringInstance || editScope === "series";

  if (!event && !selectedDate) return null;

  return (
    <div className="event-modal-overlay" ref={overlayRef} onClick={onClose}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        {/* Compact header */}
        <header className="event-modal-header">
          <button type="button" className="event-modal-close" onClick={onClose} title="Close" disabled={isSaving || isDeleting} aria-label="Close">
            {SYM.close}
          </button>
          <h2 className="event-modal-title">{isNewEvent ? "Add Event" : "Edit Event"}</h2>
          <div className="event-modal-header-actions">
            {!isNewEvent && (
              <button type="button" className="event-modal-icon-btn" onClick={handleCopy} title="Copy event" disabled={isSaving || isDeleting} aria-label="Copy event">
                {SYM.clipboard}
              </button>
            )}
            {!isNewEvent && (
              <button type="button" className="event-modal-icon-btn" onClick={handleDelete} title="Delete" disabled={isSaving || isDeleting} aria-label="Delete">
                {SYM.trash}
              </button>
            )}
            <button
              type="button"
              className="event-modal-save"
              onClick={handleSave}
              disabled={!hasChanges || isSaving || isDeleting}
              title="Save"
            >
              {isSaving ? <><span className="event-modal-spinner" /> Save</> : "Save"}
            </button>
          </div>
        </header>

        <div className="event-modal-body" ref={bodyScrollRef}>
          {isRecurringInstance && (
            <div className="event-modal-row event-modal-edit-scope">
              <span className="event-modal-label">Edit</span>
              <div className="event-modal-edit-scope-btns">
                <button
                  type="button"
                  className={`event-modal-chip ${editScope === "day" ? "event-modal-chip-on" : ""}`}
                  onClick={() => setEditScope("day")}
                >
                  This occurrence
                </button>
                <button
                  type="button"
                  className={`event-modal-chip ${editScope === "series" ? "event-modal-chip-on" : ""}`}
                  onClick={() => setEditScope("series")}
                >
                  Series
                </button>
              </div>
            </div>
          )}
          <div className="event-modal-row event-modal-row-title">
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              className="event-modal-input event-modal-input-title"
              placeholder="Event title"
              aria-label="Event title"
            />
          </div>

          <div className="event-modal-row event-modal-row-location">
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              className="event-modal-input"
              placeholder="Location (optional)"
              aria-label="Location"
            />
            <a
              href={formData.location?.trim() ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.location.trim())}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="event-modal-maps-link"
              title="Search on Google Maps"
              aria-label="Search on Google Maps"
              onClick={(e) => !formData.location?.trim() && e.preventDefault()}
            >
              <svg className="event-modal-maps-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
              </svg>
            </a>
          </div>

          <div className="event-modal-row event-modal-row-participants">
            <span className="event-modal-label">Who's going</span>
            <div className="event-modal-chips">
              {allParticipants.map(participant => (
                <button
                  key={participant}
                  type="button"
                  className={`event-modal-chip ${formData.selectedParticipants.includes(participant) ? "event-modal-chip-on" : ""}`}
                  onClick={() => handleParticipantToggle(participant)}
                >
                  {participant}
                </button>
              ))}
              <button
                type="button"
                className={`event-modal-chip event-modal-chip-all ${formData.selectedParticipants.length === 0 ? "event-modal-chip-on" : ""}`}
                onClick={handleSelectAll}
              >
                All
              </button>
            </div>
          </div>

          <div className="event-modal-row event-modal-row-datetime">
            <label className="event-modal-toggle-row">
              <input
                type="checkbox"
                checked={formData.allDay}
                onChange={(e) => handleInputChange("allDay", e.target.checked)}
                className="event-modal-checkbox"
                id="allDay"
              />
              <span className="event-modal-toggle-label">All day</span>
            </label>
            <div className="event-modal-datetime">
              <div className="event-modal-datetime-group">
                <span className="event-modal-datetime-label">Start</span>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange("startDate", e.target.value)}
                  className="event-modal-input event-modal-input-date"
                  aria-label="Start date"
                />
                {!formData.allDay && (
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange("startTime", e.target.value)}
                    onFocus={handleStartTimeFocus}
                    onBlur={handleStartTimeBlur}
                    className="event-modal-input event-modal-input-time"
                    aria-label="Start time"
                  />
                )}
              </div>
              <div className="event-modal-datetime-group">
                <span className="event-modal-datetime-label">End</span>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                  className="event-modal-input event-modal-input-date"
                  aria-label="End date"
                />
                {!formData.allDay && (
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange("endTime", e.target.value)}
                    className="event-modal-input event-modal-input-time"
                    aria-label="End time"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="event-modal-row event-modal-picker-row">
            <span className="event-modal-label">Repeats</span>
            {isRecurringInstance && editScope === "day" ? (
              <span className="event-modal-picker-value event-modal-picker-disabled">
                <span>Never</span>
              </span>
            ) : (
              <button
                type="button"
                className="event-modal-picker-value"
                onClick={() => setRepeatsModalOpen(true)}
                aria-label="Repeats"
              >
                <span>{getRecurrenceSummary(formData.recurrence)}</span>
                <span className="event-modal-picker-arrow">{SYM.chevronRight}</span>
              </button>
            )}
          </div>

          {hasActiveRecurrence && showSeriesEndDate && (
            <div className="event-modal-row event-modal-series-end-row">
              {formData.seriesEndDate ? (
                <div className="event-modal-series-end-active">
                  <span className="event-modal-label">Series ends</span>
                  <div className="event-modal-series-end-input-group">
                    <input
                      type="date"
                      value={formData.seriesEndDate}
                      onChange={(e) => handleInputChange("seriesEndDate", e.target.value)}
                      className="event-modal-input event-modal-input-date"
                      aria-label="Series end date"
                    />
                    <button
                      type="button"
                      className="event-modal-series-end-clear"
                      onClick={() => handleInputChange("seriesEndDate", "")}
                      aria-label="Remove series end date"
                      title="Remove end date"
                    >
                      {SYM.close}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="event-modal-series-end-btn"
                  onClick={() => handleInputChange("seriesEndDate", formData.startDate || format(new Date(), "yyyy-MM-dd"))}
                >
                  + Add end date for series
                </button>
              )}
            </div>
          )}

          <div className="event-modal-row event-modal-row-reminders">
            <span className="event-modal-label">Reminders</span>
            <div className="event-modal-reminders">
              {[0, 1, 2].map((i) => {
                const reminders = formData.reminders || [0, 0, 0];
                const value = reminders[i] ?? 0;
                return (
                  <select
                    key={i}
                    value={value}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 0;
                      setFormData(prev => ({
                        ...prev,
                        reminders: [...(prev.reminders || [0, 0, 0])].slice(0, 3).map((m, j) => (j === i ? v : m)),
                      }));
                      setHasChanges(true);
                    }}
                    className="event-modal-select"
                    aria-label={`Reminder ${i + 1}`}
                  >
                    {REMINDER_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                );
              })}
            </div>
          </div>

          {!isNewEvent && (
            <div className="event-modal-row event-modal-row-notify">
              <span className="event-modal-label">Notify family of change</span>
              <div className="event-modal-chips">
                {allParticipants.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className={`event-modal-chip ${(formData.notifyPersonNames || []).includes(name) ? "event-modal-chip-on" : ""}`}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        notifyPersonNames: (prev.notifyPersonNames || []).includes(name)
                          ? (prev.notifyPersonNames || []).filter(n => n !== name)
                          : [...(prev.notifyPersonNames || []), name],
                      }));
                      setHasChanges(true);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Repeats modal */}
          {repeatsModalOpen && (
            <>
              <RepeatsModal
                recurrence={formData.recurrence}
                startDate={formData.startDate}
                onDone={(recurrence) => {
                  const clearEnd = !recurrence || recurrence.frequency === "none";
                  setFormData(prev => ({ ...prev, recurrence, ...(clearEnd ? { seriesEndDate: "" } : {}) }));
                  setHasChanges(true);
                  setRepeatsModalOpen(false);
                  setCustomRecurrenceOpen(false);
                }}
                onCancel={() => {
                  setRepeatsModalOpen(false);
                  setCustomRecurrenceOpen(false);
                }}
                onOpenCustom={() => setCustomRecurrenceOpen(true)}
                weekdays={WEEKDAYS}
                weekdayLabels={WEEKDAY_LABELS}
                months={MONTHS}
                ordinalSuffix={ordinalSuffix}
              />
              {customRecurrenceOpen && (
                <CustomRecurrenceModal
                  recurrence={formData.recurrence}
                  startDate={formData.startDate}
                  onSave={(recurrence) => {
                    setFormData(prev => ({ ...prev, recurrence }));
                    setHasChanges(true);
                    setRepeatsModalOpen(false);
                    setCustomRecurrenceOpen(false);
                  }}
                  onCancel={() => setCustomRecurrenceOpen(false)}
                  weekdays={WEEKDAYS}
                  weekdayLabels={WEEKDAY_LABELS}
                  ordinals={ORDINALS}
                  months={MONTHS}
                  ordinalSuffix={ordinalSuffix}
                />
              )}
            </>
          )}

          <div className="event-modal-row event-modal-row-notes">
            {notesExpanded ? (
              <>
                <label htmlFor="notes" className="event-modal-label">Notes</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="event-modal-textarea"
                  placeholder="Add notes"
                  rows={2}
                  onBlur={() => { if (!formData.notes.trim()) setNotesExpanded(false); }}
                  autoFocus
                  aria-label="Notes"
                />
                <span className="event-modal-char-count">{formData.notes.length}/8000</span>
              </>
            ) : (
              <button
                type="button"
                className="event-modal-notes-toggle"
                onClick={() => setNotesExpanded(true)}
              >
                + Add notes
              </button>
            )}
          </div>
        </div>

        {/* Delete series confirmation */}
        {deleteConfirmOpen && (
          <div className="delete-series-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="delete-series-modal">
              <h3 className="delete-series-title">This event is one in a series.</h3>
              <div className="delete-series-options">
                <label className="delete-series-radio">
                  <input
                    type="radio"
                    name="deleteExtent"
                    checked={deleteExtent === "day"}
                    onChange={() => setDeleteExtent("day")}
                  />
                  <span>Delete just this occurrence</span>
                </label>
                <label className="delete-series-radio">
                  <input
                    type="radio"
                    name="deleteExtent"
                    checked={deleteExtent === "forward"}
                    onChange={() => setDeleteExtent("forward")}
                  />
                  <span>Delete this and all future occurrences</span>
                </label>
              </div>
              <label className="delete-series-notify">
                <input
                  type="checkbox"
                  checked={deleteNotifyAttendees}
                  onChange={(e) => setDeleteNotifyAttendees(e.target.checked)}
                />
                <span>Notify Attendees</span>
              </label>
              <div className="delete-series-actions">
                <button type="button" className="delete-series-cancel" onClick={() => setDeleteConfirmOpen(false)}>
                  CANCEL
                </button>
                <button type="button" className="delete-series-delete" onClick={handleDeleteConfirm}>
                  DELETE
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


